import { useEffect, useRef } from 'react';
import type { MutableRefObject } from 'react';
import type { ContentState } from '../store/editorStore';

interface UseSeedLessonContentOptions {
  lessonId?: string;
  lessonData?: ContentState;
  hydrationKey?: string;
  expectedLocale?: string;
  setContent: (content: ContentState) => void;
  lastSavedContentRef: MutableRefObject<string>;
  pendingSaveContentRef: MutableRefObject<string>;
  /**
   * Returns the live editor content, used to detect unsaved local edits. When
   * provided, the hook ALSO reseeds the store when fresh server data arrives for
   * the SAME context (e.g. a refetch after switching client context, where the
   * hydrationKey matches a prior visit) — but only if there are no unsaved edits,
   * so in-progress work is never clobbered. Omit to keep the seed-once-per-context
   * behavior.
   */
  getCurrentContent?: () => ContentState;
  /**
   * Whether the query backing `lessonData` has a fetch in flight. A warm
   * React Query cache (staleTime: 0, the lesson query's own default) serves
   * its last-known data synchronously on a fresh mount while refetching in
   * the background — that render is `contextChanged` too, so without this
   * flag the hook would mark the context hydrated off that still-possibly-
   * stale render, routing the *next* render (the actual settled fetch) into
   * the "protect local edits" comparison instead of applying it outright.
   * While this is true, every render for the context keeps being treated as
   * unhydrated, so the last one to resolve — the genuinely fresh one — is
   * always the one that sticks. Omit only for callers with no such race
   * (e.g. a query with a real staleTime, or that never re-observes a warm
   * cache).
   */
  isFetching?: boolean;
}

export function useSeedLessonContent({
  lessonId,
  lessonData,
  hydrationKey,
  expectedLocale,
  setContent,
  lastSavedContentRef,
  pendingSaveContentRef,
  getCurrentContent,
  isFetching,
}: UseSeedLessonContentOptions) {
  const hydratedHydrationKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!lessonId || !lessonData) return;
    if (lessonData.lessonMeta.id !== lessonId) return;
    if (
      expectedLocale &&
      lessonData.lessonMeta.locale &&
      lessonData.lessonMeta.locale !== expectedLocale
    ) return;

    const resolvedHydrationKey = hydrationKey ?? lessonId;
    const incomingSerialized = JSON.stringify(lessonData);
    const contextChanged = hydratedHydrationKeyRef.current !== resolvedHydrationKey;

    if (!contextChanged) {
      // Same context. Without a dirty-checker, preserve the seed-once behavior
      // (a refetch of the same context does not re-hydrate).
      if (!getCurrentContent) return;
      const currentSerialized = JSON.stringify(getCurrentContent());
      // Store already reflects the latest server data — nothing to do.
      if (incomingSerialized === currentSerialized) return;
      // The user has unsaved edits in this context — don't clobber them; their
      // autosave will persist and reconcile.
      if (currentSerialized !== lastSavedContentRef.current) return;
    }

    setContent(lessonData);
    lastSavedContentRef.current = incomingSerialized;
    pendingSaveContentRef.current = incomingSerialized;
    // Only mark this context hydrated once the fetch has actually settled —
    // see the `isFetching` doc above. Leaving it unmarked while a fetch is
    // still in flight makes the next render for this context apply
    // unconditionally too, instead of racing a stale baseline against it.
    if (isFetching !== true) {
      hydratedHydrationKeyRef.current = resolvedHydrationKey;
    }
  }, [
    expectedLocale,
    hydrationKey,
    isFetching,
    lessonData,
    lessonId,
    setContent,
    lastSavedContentRef,
    pendingSaveContentRef,
    getCurrentContent,
  ]);
}
